import { useState, useEffect } from 'react';

/**
 * Hook to detect online/offline status
 * Returns true when offline, false when online
 */
export const useOffline = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOffline;
};

/**
 * Hook to manage offline data persistence
 */
export const useOfflineData = <T>(key: string, initialValue: T) => {
  const [data, setData] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(data) : value;
      setData(valueToStore);
      localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  return [data, setValue] as const;
};

export interface OfflineQueueItem<TData = unknown> {
  id: string;
  action: string;
  data: TData;
}

/**
 * Hook to manage offline queue for actions
 */
export const useOfflineQueue = <TData = unknown>() => {
  const [queue, setQueue] = useOfflineData<Array<OfflineQueueItem<TData>>>(
    'offline_queue',
    []
  );

  const addToQueue = (action: string, data: TData) => {
    const id = `${Date.now()}-${Math.random()}`;
    setQueue((prev) => [...prev, { id, action, data }]);
    return id;
  };

  const removeFromQueue = (id: string) => {
    setQueue((prev) => prev.filter((item) => item.id !== id));
  };

  const processQueue = async (
    processor: (item: OfflineQueueItem<TData>) => Promise<void>
  ) => {
    const itemsToProcess = [...queue];

    for (const item of itemsToProcess) {
      try {
        await processor(item);
        removeFromQueue(item.id);
      } catch (error) {
        console.error('Failed to process queue item:', error);
        // Keep item in queue for retry
      }
    }
  };

  return { queue, addToQueue, removeFromQueue, processQueue };
};

/**
 * Hook to sync data when coming back online
 */
export const useOnlineSync = (syncFn: () => Promise<void>) => {
  const isOffline = useOffline();
  const [lastSyncTime, setLastSyncTime] = useOfflineData<number>(
    'last_sync_time',
    0
  );

  useEffect(() => {
    if (!isOffline && Date.now() - lastSyncTime > 5000) {
      // Only sync if more than 5 seconds since last sync
      syncFn()
        .then(() => {
          setLastSyncTime(Date.now());
        })
        .catch((error) => {
          console.error('Sync failed:', error);
        });
    }
  }, [isOffline]);

  return { isOffline, lastSyncTime };
};
